export type BulkTemplateCategory = { id: string; name: string };

const MAX_PRODUCT_ROWS = 2000;

/**
 * Column order:
 * TITLE, DESCRIPTION, SKU, PARENT_SKU, COMBINATION_STRING, PRICE, COST PRICE,
 * QUANTITY, IMAGE URL, AVATAR URL, CURRENCY, PUBLISHED, WEIGHT, TAGS,
 * LIMITED STOCK, INHOUSE, CATEGORY, then 7x(VARIATION_NAME_n, VARIATION_OPTIONS_n).
 */
const COL_CURRENCY = 'K';
const COL_PUBLISHED = 'L';
const COL_TAGS = 'N';
const COL_LIMITED_STOCK = 'O';
const COL_INHOUSE = 'P';
const CATEGORY_COL = 'Q';
const COL_VARIATION_NAME_1 = 'R';
const COL_VARIATION_OPTIONS_1 = 'S';
const COL_SKU = 'C';
const COL_PARENT_SKU = 'D';
const COL_COMBINATION_STRING = 'E';

const BOOLEAN_LIST_FORMULA = '"TRUE,FALSE"';

export async function buildBulkProductUploadTemplateXlsx(
  categories: BulkTemplateCategory[],
  tags: string[],
  currencies: string[],
): Promise<Blob> {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();

  workbook.creator = 'Duke Supplier Console';

  const productsSheet = workbook.addWorksheet('Products', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  const variationPairHeaders: string[] = [];
  for (let n = 1; n <= 7; n++) {
    variationPairHeaders.push(
      `VARIATION_NAME_${n}`,
      `VARIATION_OPTIONS_${n}`,
    );
  }
  const headers = [
    'TITLE',
    'DESCRIPTION',
    'SKU',
    'PARENT_SKU',
    'COMBINATION_STRING',
    'PRICE',
    'COST PRICE',
    'QUANTITY',
    'IMAGE URL',
    'AVATAR URL',
    'CURRENCY',
    'PUBLISHED',
    'WEIGHT',
    'TAGS',
    'LIMITED STOCK',
    'INHOUSE',
    'CATEGORY',
    ...variationPairHeaders,
  ];
  productsSheet.addRow(headers);
  const headerRow = productsSheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle', wrapText: true };

  productsSheet.getCell(`${COL_CURRENCY}1`).note =
    'Use a currency code from the Currencies sheet. Leave blank to use the default for your selected app country.';

  productsSheet.getCell(`${COL_PUBLISHED}1`).note =
    'Dropdown: TRUE or FALSE. Leave blank to default to TRUE when you upload.';

  productsSheet.getCell(`${COL_SKU}1`).note =
    'Parent product row: set a unique SKU. Leave empty on combination (variant) lines.';
  productsSheet.getCell(`${COL_PARENT_SKU}1`).note =
    'Combination line only: same value as a parent product SKU. Leave empty on the parent row.';
  productsSheet.getCell(`${COL_COMBINATION_STRING}1`).note =
    'One value per variation axis, separated by & (e.g. Red&Small) matching the order of VARIATION_NAME_1, VARIATION_NAME_2, ...';
  productsSheet.getCell(`${COL_VARIATION_NAME_1}1`).note =
    'First variation axis name (e.g. Color, Size). Leave blank to skip all VARIATION_NAME/OPTIONS columns for this product.';
  productsSheet.getCell(`${COL_VARIATION_OPTIONS_1}1`).note =
    'Comma-separated options for VARIATION_NAME_1. Required when name is set.';

  productsSheet.getCell(`${COL_TAGS}1`).note =
    'Use names from the Tags sheet. One tag from the list, or type several separated by commas (each must match a tag name).';

  productsSheet.getCell(`${COL_LIMITED_STOCK}1`).note =
    'Dropdown: TRUE or FALSE. Leave blank if not used.';

  productsSheet.getCell(`${COL_INHOUSE}1`).note =
    'Dropdown: TRUE or FALSE. Leave blank if not used.';

  productsSheet.getCell(`${CATEGORY_COL}1`).note =
    'Pick a category from the dropdown (names). The app resolves the id when you upload.';

  productsSheet.columns = [
    { width: 28 },
    { width: 36 },
    { width: 18 },
    { width: 16 },
    { width: 22 },
    { width: 12 },
    { width: 14 },
    { width: 12 },
    { width: 36 },
    { width: 36 },
    { width: 10 },
    { width: 12 },
    { width: 10 },
    { width: 28 },
    { width: 14 },
    { width: 10 },
    { width: 28 },
    ...Array.from({ length: 7 }, () => [{ width: 18 }, { width: 28 }]).flat(),
  ];

  const categoriesSheet = workbook.addWorksheet('Categories', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  categoriesSheet.addRow(['ID', 'Name']);
  categoriesSheet.getRow(1).font = { bold: true };
  for (const c of categories) {
    categoriesSheet.addRow([c.id, c.name]);
  }
  categoriesSheet.columns = [{ width: 40 }, { width: 32 }];

  const nt = tags.length;
  let lastTagRow = 2;
  if (nt > 0) {
    const tagsSheet = workbook.addWorksheet('Tags', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    tagsSheet.addRow(['Name']);
    tagsSheet.getRow(1).font = { bold: true };
    for (const tag of tags) {
      tagsSheet.addRow([tag]);
    }
    tagsSheet.columns = [{ width: 32 }];
    tagsSheet.getCell('A1').note =
      'Tag names are loaded from your existing products. Add tags on products first, then download the template again.';
    lastTagRow = nt + 1;
  }

  const nc = currencies.length;
  let lastCurrencyRow = 2;
  if (nc > 0) {
    const currenciesSheet = workbook.addWorksheet('Currencies', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    currenciesSheet.addRow(['Code']);
    currenciesSheet.getRow(1).font = { bold: true };
    for (const code of currencies) {
      currenciesSheet.addRow([code]);
    }
    currenciesSheet.columns = [{ width: 12 }];
    lastCurrencyRow = nc + 1;
  }

  const n = categories.length;
  const lastCategoryRow = n > 0 ? n + 1 : 2;

  const dataValidations = (
    productsSheet as unknown as {
      dataValidations: {
        add: (range: string, opts: Record<string, unknown>) => void;
      };
    }
  ).dataValidations;

  const addBooleanColumn = (
    col: string,
    errorTitle: string,
    error: string,
  ) => {
    dataValidations.add(`${col}2:${col}${MAX_PRODUCT_ROWS}`, {
      type: 'list',
      allowBlank: true,
      formulae: [BOOLEAN_LIST_FORMULA],
      showErrorMessage: true,
      errorStyle: 'warning',
      errorTitle,
      error,
    });
  };

  addBooleanColumn(
    COL_PUBLISHED,
    'Published',
    'Choose TRUE or FALSE from the list.',
  );
  addBooleanColumn(
    COL_LIMITED_STOCK,
    'Limited stock',
    'Choose TRUE or FALSE from the list.',
  );
  addBooleanColumn(
    COL_INHOUSE,
    'In-house',
    'Choose TRUE or FALSE from the list.',
  );

  if (nc > 0) {
    const curListRef = `'Currencies'!$A$2:$A$${lastCurrencyRow}`;
    dataValidations.add(
      `${COL_CURRENCY}2:${COL_CURRENCY}${MAX_PRODUCT_ROWS}`,
      {
        type: 'list',
        allowBlank: true,
        formulae: [curListRef],
        showErrorMessage: true,
        errorStyle: 'warning',
        errorTitle: 'Currency',
        error: 'Pick a currency code from the Currencies sheet.',
      },
    );
  }

  if (nt > 0) {
    const tagListRef = `'Tags'!$A$2:$A$${lastTagRow}`;
    dataValidations.add(
      `${COL_TAGS}2:${COL_TAGS}${MAX_PRODUCT_ROWS}`,
      {
        type: 'list',
        allowBlank: true,
        formulae: [tagListRef],
        showErrorMessage: true,
        errorStyle: 'warning',
        errorTitle: 'Tags',
        error: 'Pick a tag from the list, or type comma-separated names that appear on the Tags sheet.',
      },
    );
  }

  if (n > 0) {
    const nameListRef = `'Categories'!$B$2:$B$${lastCategoryRow}`;
    dataValidations.add(
      `${CATEGORY_COL}2:${CATEGORY_COL}${MAX_PRODUCT_ROWS}`,
      {
        type: 'list',
        allowBlank: true,
        formulae: [nameListRef],
        showErrorMessage: true,
        errorStyle: 'warning',
        errorTitle: 'Category',
        error: 'Choose a category name from the list.',
      },
    );
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
